import type { ThreadDetail, ThreadView } from '@tabel/shared/schemas/agent-client';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Archive, ArrowLeft, Check, MessagesSquare, Pencil, Plus, ShieldAlert, X } from 'lucide-react';
import { type RefObject, useLayoutEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ErrorCodes, errorCode, errorSentence, useToast } from '../../components/ErrorToast';
import { AgentMenu } from '../../components/assistant/AgentMenu';
import { useAssistant } from '../../components/assistant/AssistantPanel';
import { Conversation } from '../../components/assistant/Conversation';
import { ProjectSidePanel } from '../../components/assistant/ProjectPanel';
import { HINTS, LABEL, T, TITLE, agentName, say } from '../../components/assistant/texts';
import {
  type AgentChoice,
  defaultChoice,
  readChoice,
  saveChoice,
  threadKey,
  threadsKey,
  useAgentComputers,
} from '../../components/assistant/useThread';
import {
  Button,
  Card,
  EmptyState,
  IconButton,
  PageHeader,
  inputCls,
  useNarrowScreen,
} from '../../components/ui';
import { listThreads, patchThread } from '../../lib/agent-api';
import { useAgentSocket } from '../../lib/agent-ws';
import { useSetCrumbs } from '../../lib/crumbs';
import { ApiError } from '../../lib/errors';
import { NAV_PATH } from '../../lib/nav';
import type { AuthedPageProps } from '../../page-route';
import { ThreadList } from './ThreadList';

/*
 * Страница «Помощник» (T077, T115, T090; contracts/ui.md §1; US3-7, US7; мокап Assistant.dc.html).
 * - Заголовок страницы: «Помощник», компьютер и «в сети», «Новый разговор».
 * - Карточка: слева «Мои разговоры» (поиск, «Архив · N разговоров»), справа — разговор колонкой до 760 px: тот же
 *   «Разговор», что в правой панели. Шапка разговора — название, агент и модель, «Переименовать разговор», «Убрать
 *   разговор в архив» (без подтверждения, тост «Отменить»).
 * - Адрес: `?thread=<id>` — разговор, `?new=1` — новый разговор, `?archive=1` — список архива. Без разговора в адресе на
 *   широком экране открыт последний разговор; до 900 px — список, разговор открывается поверх, со стрелкой «Мои
 *   разговоры».
 * - «Открыть проект» — правая панель рядом с лентой (`ProjectSidePanel`), на телефоне — на весь экран.
 * - Карточка занимает высоту окна под заголовком: лента прокручивается внутри, клавиатура телефона поле не закрывает.
 * Право — `agent.thread` и «ИИ-агент»; бригадир и кадры страницы в меню не видят, а по адресу — «Помощник недоступен».
 */

/** Высота карточки — до низа окна: лента прокручивается внутри неё. */
function useFillHeight(ref: RefObject<HTMLElement | null>, min = 420): number {
  const [h, setH] = useState(min);
  useLayoutEffect(() => {
    const el = ref.current;
    const scroller = el?.closest('main');
    if (!el || !scroller) return;
    const measure = () => {
      const parent = el.parentElement;
      let below = parent ? Number.parseFloat(getComputedStyle(parent).paddingBottom) || 0 : 0;
      for (let n = el.nextElementSibling; n; n = n.nextElementSibling)
        if (n instanceof HTMLElement)
          below += n.offsetHeight + (Number.parseFloat(getComputedStyle(n).marginTop) || 0);
      const box = scroller.getBoundingClientRect();
      const vv = window.visualViewport;
      const view = vv ? Math.min(box.bottom, vv.offsetTop + vv.height) : box.bottom;
      const top = el.getBoundingClientRect().top;
      setH(Math.max(min, Math.floor(view - top - below)));
    };
    measure();
    window.addEventListener('resize', measure);
    window.visualViewport?.addEventListener('resize', measure);
    const ro = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure);
    if (el.parentElement) ro?.observe(el.parentElement);
    return () => {
      window.removeEventListener('resize', measure);
      window.visualViewport?.removeEventListener('resize', measure);
      ro?.disconnect();
    };
  }, [ref, min]);
  return h;
}

/** Название разговора: правка на месте. Enter — сохранить, Esc — отмена; ФИО в названии сервер не примет (R19). */
function TitleEdit({ thread, onDone }: { thread: ThreadView; onDone: () => void }) {
  const qc = useQueryClient();
  const [value, setValue] = useState(thread.title);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    const title = value.trim();
    if (!title || title === thread.title) return onDone();
    setBusy(true);
    try {
      const t = await patchThread(thread.id, { title });
      qc.setQueryData<ThreadDetail>(threadKey(t.id), (d) => (d ? { ...d, thread: t } : d));
      void qc.invalidateQueries({ queryKey: ['agent-threads'] });
      onDone();
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  };
  const pd = error instanceof ApiError && error.code === 'PERSONAL_DATA';
  return (
    <div className="flex min-w-0 flex-1 flex-col gap-1 py-2">
      <div className="flex items-center gap-1">
        <input
          // biome-ignore lint/a11y/noAutofocus: правка названия начата нажатием «Переименовать разговор»
          autoFocus
          value={value}
          maxLength={80}
          aria-label={LABEL.title}
          aria-invalid={error ? true : undefined}
          onChange={(e) => {
            setValue(e.target.value);
            setError(null);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              void save();
            } else if (e.key === 'Escape') {
              e.preventDefault();
              e.stopPropagation();
              onDone();
            }
          }}
          className={`${inputCls} min-w-0 flex-1`}
        />
        <IconButton icon={Check} label={LABEL.save} disabled={busy} onClick={() => void save()} />
        <IconButton icon={X} label={LABEL.cancel} onClick={onDone} />
      </div>
      {error !== null && (
        <p role="alert" className="text-[12px] text-bad">
          {pd && error instanceof ApiError ? error.message : `${T.renameFailed} ${errorSentence(error)}`}
          {!pd && error instanceof ApiError && error.requestId && (
            <span className="block text-ink-2">
              {LABEL.errorCode} <ErrorCodes requestId={error.requestId} code={errorCode(error)} />
            </span>
          )}
        </p>
      )}
    </div>
  );
}

export function AssistantPage({ me }: AuthedPageProps) {
  const tz = me.object.tzOffset;
  const assistant = useAssistant();
  const computers = useAgentComputers();
  const available = computers.available && assistant !== null;
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { showInfo, showError } = useToast();
  const narrow = useNarrowScreen();
  const [params, setParams] = useSearchParams();
  const archiveView = params.get('archive') === '1';
  const wantNew = params.get('new') === '1';
  const [query, setQuery] = useState('');
  const [project, setProject] = useState<number | null>(null);
  const [renaming, setRenaming] = useState(false);
  const [choice, setChoice] = useState<AgentChoice | null>(() => readChoice());
  const pick = defaultChoice(computers.list, choice);
  const card = useRef<HTMLDivElement>(null);
  const height = useFillHeight(card);

  const active = useQuery({
    queryKey: threadsKey(false),
    queryFn: () => listThreads(false),
    enabled: available,
  });
  const archived = useQuery({
    queryKey: threadsKey(true),
    queryFn: () => listThreads(true),
    enabled: available,
  });
  // Состояние разговоров («работает», последний ответ) — по WebSocket: список заново.
  useAgentSocket((e) => {
    if (e.op === 'thread' || (e.op === 'open' && e.reconnected)) void active.refetch();
  }, available);

  const list = archiveView ? archived : active;
  const asked = params.get('thread');
  // Широкий экран без разговора в адресе — последний разговор; до 900 px — список.
  const threadId = wantNew
    ? null
    : (asked ?? (narrow || archiveView ? null : (active.data?.[0]?.id ?? null)));
  const open = wantNew || asked !== null;
  const showConversation = !narrow || open;
  const showList = !narrow || !open;
  const listed = [...(active.data ?? []), ...(archived.data ?? [])].find((t) => t.id === threadId) ?? null;

  const go = (next: Record<string, string | null>) => {
    const p = new URLSearchParams(params);
    for (const [k, v] of Object.entries(next)) {
      if (v === null) p.delete(k);
      else p.set(k, v);
    }
    setParams(p);
  };
  const select = (id: string) => {
    setRenaming(false);
    setProject(null);
    go({ thread: id, new: null });
  };
  const startNew = () => {
    setRenaming(false);
    setProject(null);
    go({ thread: null, new: '1', archive: null });
  };

  const archive = async (t: ThreadView) => {
    try {
      const next = await patchThread(t.id, { archived: true });
      qc.setQueryData<ThreadDetail>(threadKey(t.id), (d) => (d ? { ...d, thread: next } : d));
      void qc.invalidateQueries({ queryKey: ['agent-threads'] });
      go({ thread: null, new: null });
      showInfo(T.archivedToast, {
        action: {
          label: LABEL.undo,
          onClick: () =>
            void patchThread(t.id, { archived: false })
              .then((back) => {
                qc.setQueryData<ThreadDetail>(threadKey(back.id), (d) => (d ? { ...d, thread: back } : d));
                void qc.invalidateQueries({ queryKey: ['agent-threads'] });
                go({ thread: t.id, new: null, archive: null });
              })
              .catch((e) => showError(e, { screen: 'Assistant', action: 'restore thread' })),
        },
      });
    } catch (e) {
      showError(e, { screen: 'Assistant', action: 'archive thread' });
    }
  };

  const pc = computers.computer(pick?.connectorId);
  useSetCrumbs(
    showConversation && listed && open
      ? [{ label: TITLE.page, to: NAV_PATH.assistant }, { label: listed.title }]
      : [{ label: TITLE.page }],
  );

  if (!available)
    return (
      <>
        <PageHeader title={TITLE.page} />
        <Card>
          <EmptyState icon={ShieldAlert} title={TITLE.unavailable} description={T.unavailable} />
        </Card>
      </>
    );

  // До 900 px открытый разговор — слоем на всю карточку: заголовок страницы уступает место ленте (ux.md, «Входящие»).
  const layer = narrow && open;
  return (
    <>
      {/* Разговор слоем закрывает заголовок страницы; заголовок первого уровня остаётся для чтения с экрана (axe
          `page-has-heading-one`). */}
      {layer && <h1 className="sr-only">{TITLE.page}</h1>}
      {!layer && (
        <PageHeader
          title={TITLE.page}
          description={say.pageIntro(pc?.name ?? null)}
          actions={
            <>
              {pc && (
                <span className="inline-flex items-center gap-2 rounded-full border border-line bg-surface px-2.5 py-1 text-[12.5px] text-ink-2">
                  <span
                    aria-hidden
                    className={`h-2 w-2 shrink-0 rounded-full ${pc.online ? 'bg-ok' : 'bg-warn'}`}
                  />
                  {say.computerChip(pc.name, pc.online)}
                </span>
              )}
              <Button icon={Plus} onClick={startNew}>
                {LABEL.newThread}
              </Button>
            </>
          }
        />
      )}
      <div
        ref={card}
        style={{ height }}
        className="flex min-h-0 overflow-hidden rounded-card border border-line bg-surface shadow-card"
      >
        {showList && (
          <div
            className={`flex min-h-0 flex-col ${narrow ? 'w-full' : 'w-[280px] shrink-0 border-r border-line'}`}
          >
            <ThreadList
              threads={list.data}
              archivedCount={archived.data?.length ?? 0}
              archiveView={archiveView}
              selectedId={open || !narrow ? threadId : null}
              query={query}
              onQuery={setQuery}
              onSelect={select}
              onToggleArchive={() => go({ archive: archiveView ? null : '1' })}
              loading={list.isLoading}
              error={list.error}
              onRetry={() => void list.refetch()}
              tz={tz}
            />
          </div>
        )}
        {showConversation && (
          <Conversation
            threadId={threadId}
            page="assistant"
            choice={pick}
            hints={HINTS.assistant}
            tz={tz}
            layout="page"
            onCreated={(t) => go({ thread: t.id, new: null })}
            onOpenProject={setProject}
            onNewThread={startNew}
            onOpenMyAgents={() => navigate(NAV_PATH.myAgents)}
          >
            {(parts) => (
              <section
                aria-label={parts.thread?.title ?? listed?.title ?? TITLE.newThread}
                className="flex min-w-0 flex-1 flex-col"
              >
                <header className="flex min-h-[52px] shrink-0 items-center gap-1 border-b border-line pr-3 pl-6 max-sm:pl-2">
                  {narrow && (
                    <IconButton
                      icon={ArrowLeft}
                      label={LABEL.backToList}
                      onClick={() => go({ thread: null, new: null })}
                    />
                  )}
                  {renaming && parts.thread ? (
                    <TitleEdit thread={parts.thread} onDone={() => setRenaming(false)} />
                  ) : (
                    <div className="flex min-w-0 flex-1 flex-col py-1.5 leading-[1.4]">
                      <h2 className="truncate text-[15px] font-semibold text-ink">
                        {parts.thread?.title ?? listed?.title ?? (threadId ? LABEL.loading : TITLE.newThread)}
                      </h2>
                      {parts.thread && (
                        <p className="truncate text-[12px] text-ink-2">
                          {say.threadMeta(agentName(parts.thread.agent), parts.thread.model)}
                        </p>
                      )}
                    </div>
                  )}
                  {threadId === null ? (
                    <AgentMenu
                      computers={computers.list}
                      value={pick}
                      locked={false}
                      onPick={(c) => {
                        saveChoice(c);
                        setChoice(c);
                      }}
                    />
                  ) : (
                    !renaming &&
                    parts.thread && (
                      <>
                        <IconButton icon={Pencil} label={LABEL.rename} onClick={() => setRenaming(true)} />
                        {!parts.thread.archived && (
                          <IconButton
                            icon={Archive}
                            label={LABEL.archiveThread}
                            onClick={() => parts.thread && void archive(parts.thread)}
                          />
                        )}
                      </>
                    )
                  )}
                </header>
                {parts.status}
                {parts.feed}
                {parts.dock && (
                  <div className="shrink-0 border-t border-line px-6 pt-3 pb-4 max-sm:px-3 max-sm:pb-[calc(12px+env(safe-area-inset-bottom))]">
                    {parts.dock}
                  </div>
                )}
              </section>
            )}
          </Conversation>
        )}
        {!showConversation && !showList && (
          <EmptyState icon={MessagesSquare} title={TITLE.noThreads} description={T.noThreads} />
        )}
      </div>
      {project !== null && <ProjectSidePanel planId={project} tz={tz} onClose={() => setProject(null)} />}
    </>
  );
}
